import { isWallId, type WallId } from "./wallDefinitions";
import {
  normalizeMpWallAssignments,
  type MpWallAssignments,
} from "./mpWallAssignments";

export interface WallState {
  activeWallId: WallId | null;
  mpWallAssignments: MpWallAssignments | null;
  // Accumulated game-time elapsed for the currently active wall run. Grows
  // with the model's game-speed-adjusted `dtSeconds`, not wall-clock time, so
  // admin/system speed multipliers compress the run uniformly. `null` while no
  // wall is active.
  wallRunElapsedMs: number | null;
  stepWallBestClearMs: number | null;
  priceWallBestClearMs: number | null;
  mpWallBestClearMs: number | null;
  freshnessWallBestClearMs: number | null;
  outerWallBestClearMs: number | null;
  splitWallBestClearMs: number | null;
  hgWallBestClearMs: number | null;
  stockWallBestClearMs: number | null;
  amplificationWallBestClearMs: number | null;
  stockWallNhovUsedCount: number;
  // Game-time elapsed since the last Hov-run boundary (wall start / MHov /
  // NHov / OHov) while the Freshness Wall is active. Non-null only inside a
  // Freshness Wall run; resets to 0 on each Hov-run update so freshness staling
  // is evaluated strictly inside the wall-active boundary.
  freshnessElapsedSinceHovRunMs: number | null;
  unseenClearedWallIds: readonly WallId[];
}

export function createDefaultWallState(): WallState {
  return {
    activeWallId: null,
    mpWallAssignments: null,
    wallRunElapsedMs: null,
    stepWallBestClearMs: null,
    priceWallBestClearMs: null,
    mpWallBestClearMs: null,
    freshnessWallBestClearMs: null,
    outerWallBestClearMs: null,
    splitWallBestClearMs: null,
    hgWallBestClearMs: null,
    stockWallBestClearMs: null,
    amplificationWallBestClearMs: null,
    stockWallNhovUsedCount: 0,
    freshnessElapsedSinceHovRunMs: null,
    unseenClearedWallIds: [],
  };
}

export function cloneWallState(state: WallState): WallState {
  return {
    activeWallId: state.activeWallId,
    mpWallAssignments:
      state.activeWallId === "mp-wall"
        ? normalizeMpWallAssignments(state.mpWallAssignments)
        : null,
    wallRunElapsedMs: state.wallRunElapsedMs,
    stepWallBestClearMs: state.stepWallBestClearMs,
    priceWallBestClearMs: state.priceWallBestClearMs,
    mpWallBestClearMs: state.mpWallBestClearMs,
    freshnessWallBestClearMs: state.freshnessWallBestClearMs,
    outerWallBestClearMs: state.outerWallBestClearMs,
    splitWallBestClearMs: state.splitWallBestClearMs,
    hgWallBestClearMs: state.hgWallBestClearMs,
    stockWallBestClearMs: state.stockWallBestClearMs,
    amplificationWallBestClearMs: state.amplificationWallBestClearMs,
    stockWallNhovUsedCount: state.stockWallNhovUsedCount,
    freshnessElapsedSinceHovRunMs: state.freshnessElapsedSinceHovRunMs,
    unseenClearedWallIds: [...state.unseenClearedWallIds],
  };
}

export function normalizeWallState(input: unknown): WallState {
  const defaults = createDefaultWallState();
  if (input === null || typeof input !== "object") {
    return defaults;
  }
  const source = input as Partial<WallState>;
  const activeWallId =
    typeof source.activeWallId === "string" && isWallId(source.activeWallId)
      ? source.activeWallId
      : null;
  const wallRunElapsedMs = normalizeOptionalNonNegativeNumber(
    source.wallRunElapsedMs,
  );
  const stepWallBestClearMs = normalizeOptionalPositiveNumber(
    source.stepWallBestClearMs,
  );
  const priceWallBestClearMs = normalizeOptionalPositiveNumber(
    source.priceWallBestClearMs,
  );
  const mpWallBestClearMs = normalizeOptionalPositiveNumber(
    source.mpWallBestClearMs,
  );
  const freshnessWallBestClearMs = normalizeOptionalPositiveNumber(
    source.freshnessWallBestClearMs,
  );
  const outerWallBestClearMs = normalizeOptionalPositiveNumber(
    source.outerWallBestClearMs,
  );
  const splitWallBestClearMs = normalizeOptionalPositiveNumber(
    source.splitWallBestClearMs,
  );
  const hgWallBestClearMs = normalizeOptionalPositiveNumber(
    source.hgWallBestClearMs,
  );
  const stockWallBestClearMs = normalizeOptionalPositiveNumber(
    source.stockWallBestClearMs,
  );
  const amplificationWallBestClearMs = normalizeOptionalPositiveNumber(
    source.amplificationWallBestClearMs,
  );
  const stockWallNhovUsedCount = normalizeNonNegativeInteger(
    source.stockWallNhovUsedCount,
  );
  const freshnessElapsedSinceHovRunMs = normalizeOptionalNonNegativeNumber(
    source.freshnessElapsedSinceHovRunMs,
  );
  const unseenClearedWallIds = normalizeUnseenClearedWallIds(
    source.unseenClearedWallIds,
  );
  return {
    activeWallId,
    mpWallAssignments:
      activeWallId === "mp-wall"
        ? normalizeMpWallAssignments(source.mpWallAssignments)
        : null,
    wallRunElapsedMs: activeWallId === null ? null : (wallRunElapsedMs ?? 0),
    stepWallBestClearMs,
    priceWallBestClearMs,
    mpWallBestClearMs,
    freshnessWallBestClearMs,
    outerWallBestClearMs,
    splitWallBestClearMs,
    hgWallBestClearMs,
    stockWallBestClearMs,
    amplificationWallBestClearMs,
    stockWallNhovUsedCount:
      activeWallId === "stock-wall" ? stockWallNhovUsedCount : 0,
    freshnessElapsedSinceHovRunMs:
      activeWallId === "freshness-wall"
        ? (freshnessElapsedSinceHovRunMs ?? 0)
        : null,
    unseenClearedWallIds,
  };
}

function normalizeOptionalNonNegativeNumber(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return null;
  }
  return value;
}

function normalizeOptionalPositiveNumber(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return null;
  }
  return value;
}

function normalizeNonNegativeInteger(value: unknown): number {
  if (
    typeof value !== "number" ||
    !Number.isFinite(value) ||
    !Number.isInteger(value) ||
    value < 0
  ) {
    return 0;
  }
  return value;
}

function normalizeUnseenClearedWallIds(value: unknown): readonly WallId[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<WallId>();
  const result: WallId[] = [];
  for (const entry of value) {
    if (typeof entry !== "string" || !isWallId(entry)) continue;
    if (seen.has(entry)) continue;
    seen.add(entry);
    result.push(entry);
  }
  return result;
}

export function isWallActive(state: WallState, wallId: WallId): boolean {
  return state.activeWallId === wallId;
}

// Shared lifecycle reducers — own the cross-wall fields (activeWallId,
// wallRunElapsedMs, unseenClearedWallIds). Per-wall slices are mutated by
// each wall's WallChallengePolicy via `applyClearRecord`.

export function beginWallRunInState(
  state: WallState,
  wallId: WallId,
): WallState {
  return {
    ...state,
    activeWallId: wallId,
    mpWallAssignments:
      wallId === "mp-wall" ? normalizeMpWallAssignments(null) : null,
    wallRunElapsedMs: 0,
    stockWallNhovUsedCount: 0,
    freshnessElapsedSinceHovRunMs: wallId === "freshness-wall" ? 0 : null,
  };
}

export function advanceWallRunElapsedInState(
  state: WallState,
  gameElapsedMs: number,
): WallState {
  if (state.activeWallId === null) return state;
  if (!Number.isFinite(gameElapsedMs) || gameElapsedMs <= 0) return state;
  const current = state.wallRunElapsedMs ?? 0;
  return {
    ...state,
    wallRunElapsedMs: current + gameElapsedMs,
    freshnessElapsedSinceHovRunMs:
      state.freshnessElapsedSinceHovRunMs !== null
        ? state.freshnessElapsedSinceHovRunMs + gameElapsedMs
        : null,
  };
}

export function clearActiveWallRunInState(state: WallState): WallState {
  if (state.activeWallId === null) return state;
  return {
    ...state,
    activeWallId: null,
    mpWallAssignments: null,
    wallRunElapsedMs: null,
    stockWallNhovUsedCount: 0,
    freshnessElapsedSinceHovRunMs: null,
  };
}

export function incrementStockWallNhovUsedCountInState(
  state: WallState,
): WallState {
  if (state.activeWallId !== "stock-wall") return state;
  return {
    ...state,
    stockWallNhovUsedCount: state.stockWallNhovUsedCount + 1,
  };
}

// Resets the freshness elapsed counter to 0 on a Hov-run boundary (MHov /
// NHov / OHov / wall-start). No-op when the counter is null, so this can be
// called unconditionally from shared reset hooks.
export function resetFreshnessElapsedInState(state: WallState): WallState {
  if (state.freshnessElapsedSinceHovRunMs === null) return state;
  if (state.freshnessElapsedSinceHovRunMs === 0) return state;
  return {
    ...state,
    freshnessElapsedSinceHovRunMs: 0,
  };
}

export function addUnseenClearedWall(
  state: WallState,
  wallId: WallId,
): WallState {
  if (state.unseenClearedWallIds.includes(wallId)) return state;
  return {
    ...state,
    unseenClearedWallIds: [...state.unseenClearedWallIds, wallId],
  };
}

export function clearUnseenClearedWall(
  state: WallState,
  wallId: WallId,
): WallState {
  if (!state.unseenClearedWallIds.includes(wallId)) return state;
  return {
    ...state,
    unseenClearedWallIds: state.unseenClearedWallIds.filter(
      (id) => id !== wallId,
    ),
  };
}
